import React, { useState, useEffect, useCallback } from "react";
import {
  doc, getDoc, setDoc, updateDoc, deleteDoc, deleteField,
} from "firebase/firestore";
import { db } from "../firebase";
import { orderReviewUrl } from "../shop";

// ── Test order configuration ───────────────────────────────────────────────────

const TEST_EMAIL = "tobias.boldt@gmail.com";

const TEST_ORDERS = [
  { sourceId: "4010638823", testId: "TEST_4010638823" },
  { sourceId: "4018680076", testId: "TEST_4018680076" },
  { sourceId: "4014466220", testId: "TEST_4014466220" },
];

const TRACKING_FIELDS = [
  "welcomeSentAt",
  "orderReviewedAt",
  "orderDecision",
  "orderFeedback",
  "orderReminder1SentAt",
  "orderReminder2SentAt",
  "orderReminder3SentAt",
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtTs = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const fieldRow = (label, value) => (
  <div style={{ display: "flex", gap: "0.5em",
    fontSize: "0.8rem", marginBottom: "0.25em" }}>
    <span style={{ color: "#a0aec0", minWidth: "160px" }}>{label}</span>
    <span style={{ color: "#2d3748", fontWeight: value && value !== "—" ? "600" : "400" }}>
      {value || "—"}
    </span>
  </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const TestOrdersPage = () => {
  const [showInUpload, setShowInUpload] = useState(
    () => localStorage.getItem("showTestOrdersInUpload") === "true",
  );

  const toggleShowInUpload = (e) => {
    const val = e.target.checked;
    setShowInUpload(val);
    localStorage.setItem("showTestOrdersInUpload", val ? "true" : "false");
  };

  // Per-test-order state: { sourceData, testData, loading, acting }
  const [orders, setOrders] = useState(
    TEST_ORDERS.map((t) => ({
      ...t,
      sourceData: null,
      testData: null,
      sourceLoading: true,
      testLoading: true,
      acting: false,
    })),
  );
  const [creatingAll, setCreatingAll] = useState(false);

  const updateOrder = useCallback((testId, patch) => {
    setOrders((prev) =>
      prev.map((o) => (o.testId === testId ? { ...o, ...patch } : o)),
    );
  }, []);

  // Load source + test docs for each order
  useEffect(() => {
    TEST_ORDERS.forEach(({ sourceId, testId }) => {
      // Load source
      getDoc(doc(db, "etsy_orders", sourceId)).then((snap) => {
        updateOrder(testId, {
          sourceData: snap.exists() ? snap.data() : null,
          sourceLoading: false,
        });
      }).catch(() => updateOrder(testId, { sourceData: null, sourceLoading: false }));

      // Load test copy
      getDoc(doc(db, "etsy_orders", testId)).then((snap) => {
        updateOrder(testId, {
          testData: snap.exists() ? { id: snap.id, ...snap.data() } : null,
          testLoading: false,
        });
      }).catch(() => updateOrder(testId, { testData: null, testLoading: false }));
    });
  }, [updateOrder]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const createTestOrder = async ({ sourceId, testId, sourceData }) => {
    if (!sourceData) {
      alert(`Quell-Bestellung ${sourceId} nicht in Firestore gefunden.`);
      return;
    }
    updateOrder(testId, { acting: true });
    try {
      const data = { ...sourceData };
      data.customerEmail = TEST_EMAIL;
      data.workflowStage = "test";
      data.isTestOrder   = true;
      TRACKING_FIELDS.forEach((f) => delete data[f]);
      await setDoc(doc(db, "etsy_orders", testId), data);
      updateOrder(testId, {
        testData: { id: testId, ...data },
        acting: false,
      });
    } catch (err) {
      alert("Anlegen fehlgeschlagen: " + err.message);
      updateOrder(testId, { acting: false });
    }
  };

  const resetTestOrder = async ({ testId }) => {
    updateOrder(testId, { acting: true });
    try {
      const patch = {};
      TRACKING_FIELDS.forEach((f) => { patch[f] = deleteField(); });
      await updateDoc(doc(db, "etsy_orders", testId), patch);
      // Refresh test data
      const snap = await getDoc(doc(db, "etsy_orders", testId));
      updateOrder(testId, {
        testData: snap.exists() ? { id: snap.id, ...snap.data() } : null,
        acting: false,
      });
    } catch (err) {
      alert("Zurücksetzen fehlgeschlagen: " + err.message);
      updateOrder(testId, { acting: false });
    }
  };

  const deleteTestOrder = async ({ testId }) => {
    if (!window.confirm(`Testbestellung ${testId} wirklich löschen?`)) return;
    updateOrder(testId, { acting: true });
    try {
      await deleteDoc(doc(db, "etsy_orders", testId));
      updateOrder(testId, { testData: null, acting: false });
    } catch (err) {
      alert("Löschen fehlgeschlagen: " + err.message);
      updateOrder(testId, { acting: false });
    }
  };

  const createAllMissing = async () => {
    setCreatingAll(true);
    for (const o of orders) {
      if (!o.testData && o.sourceData) {
        await createTestOrder(o);
      }
    }
    setCreatingAll(false);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  const allExist  = orders.every((o) => !!o.testData);
  const anyMissing = orders.some((o) => !o.testData && !!o.sourceData);

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "1.25em", gap: "1em", flexWrap: "wrap",
      }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", color: "#1a202c" }}>
          Test-Bestellungen
        </h2>
        {!allExist && (
          <button
            onClick={createAllMissing}
            disabled={creatingAll || !anyMissing}
            style={{
              padding: "0.5em 1.4em", borderRadius: "7px", border: "none",
              background: creatingAll ? "#a0aec0" : "#553c9a",
              color: "#fff", fontWeight: "700", fontSize: "0.875rem",
              cursor: creatingAll ? "not-allowed" : "pointer",
            }}
          >
            {creatingAll ? "Wird angelegt…" : "Alle anlegen"}
          </button>
        )}
      </div>

      {/* Info banner */}
      <div style={{
        background: "#faf5ff", border: "1px solid #b794f4",
        borderRadius: "8px", padding: "0.75em 1.25em",
        marginBottom: "1.5em", fontSize: "0.82rem", color: "#44337a",
      }}>
        <strong>Testmodus:</strong> Diese Bestellungen sind Kopien realer Etsy-Orders,
        E-Mails gehen an <strong>{TEST_EMAIL}</strong>. Der Scraper archiviert sie nicht
        (workflowStage: "test"). Zum erneuten Testen auf "Zurücksetzen" klicken.
        <label style={{
          display: "flex", alignItems: "center", gap: "0.5em",
          marginTop: "0.6em", cursor: "pointer", userSelect: "none",
        }}>
          <input
            type="checkbox"
            checked={showInUpload}
            onChange={toggleShowInUpload}
            style={{ accentColor: "#553c9a", width: "15px", height: "15px" }}
          />
          <span>Testbestellungen in <strong>Entwurf hochladen</strong> anzeigen</span>
        </label>
      </div>

      {/* Order cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: "1.25em" }}>
        {orders.map((o) => {
          const td = o.testData;
          const loading = o.sourceLoading || o.testLoading;

          return (
            <div key={o.testId} style={{
              background: "#fff", borderRadius: "10px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              padding: "1.5em",
              borderLeft: `4px solid ${td ? "#b794f4" : "#e2e8f0"}`,
            }}>
              {/* Card header */}
              <div style={{
                display: "flex", alignItems: "flex-start",
                justifyContent: "space-between", marginBottom: "1em",
                gap: "1em", flexWrap: "wrap",
              }}>
                <div>
                  <div style={{ fontWeight: "700", fontSize: "0.95rem",
                    color: "#1a202c", marginBottom: "0.2em" }}>
                    {o.testId}
                    {" "}
                    {td ? (
                      <span style={{
                        background: "#faf5ff", color: "#553c9a",
                        border: "1px solid #b794f4",
                        padding: "0.15em 0.55em", borderRadius: "10px",
                        fontSize: "0.72rem", fontWeight: "700",
                        verticalAlign: "middle",
                      }}>
                        Test
                      </span>
                    ) : (
                      <span style={{
                        background: "#edf2f7", color: "#718096",
                        border: "1px solid #cbd5e0",
                        padding: "0.15em 0.55em", borderRadius: "10px",
                        fontSize: "0.72rem", fontWeight: "600",
                        verticalAlign: "middle",
                      }}>
                        Nicht angelegt
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#718096" }}>
                    Quelle: {o.sourceId}
                    {o.sourceData
                      ? ` · ${o.sourceData.buyerName || "–"}`
                      : " · (nicht in Firestore)"}
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: "flex", gap: "0.5em", flexWrap: "wrap" }}>
                  {!loading && !td && (
                    <button
                      onClick={() => createTestOrder(o)}
                      disabled={o.acting || !o.sourceData}
                      style={{
                        padding: "0.4em 1em", borderRadius: "6px",
                        border: "none",
                        background: o.acting ? "#a0aec0" : "#553c9a",
                        color: "#fff", fontWeight: "600", fontSize: "0.82rem",
                        cursor: o.acting ? "not-allowed" : "pointer",
                      }}
                    >
                      {o.acting ? "…" : "Anlegen"}
                    </button>
                  )}
                  {!loading && td && (
                    <>
                      <button
                        onClick={() => resetTestOrder(o)}
                        disabled={o.acting}
                        style={{
                          padding: "0.4em 1em", borderRadius: "6px",
                          border: "1px solid #b794f4", background: "#fff",
                          color: "#553c9a", fontWeight: "600", fontSize: "0.82rem",
                          cursor: o.acting ? "not-allowed" : "pointer",
                        }}
                      >
                        {o.acting ? "…" : "Zurücksetzen"}
                      </button>
                      <button
                        onClick={() => deleteTestOrder(o)}
                        disabled={o.acting}
                        style={{
                          padding: "0.4em 1em", borderRadius: "6px",
                          border: "1px solid #fc8181", background: "#fff",
                          color: "#c53030", fontWeight: "600", fontSize: "0.82rem",
                          cursor: o.acting ? "not-allowed" : "pointer",
                        }}
                      >
                        {o.acting ? "…" : "Löschen"}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Fields */}
              {loading ? (
                <p style={{ margin: 0, color: "#a0aec0",
                  fontSize: "0.8rem", fontStyle: "italic" }}>
                  Lade…
                </p>
              ) : td ? (
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr",
                  gap: "0 2em",
                }}>
                  <div>
                    {fieldRow("E-Mail", td.customerEmail)}
                    {fieldRow("Käufer", td.buyerName)}
                    {fieldRow("Versand bis", td.shipByDate)}
                    {fieldRow("Stage", td.workflowStage)}
                  </div>
                  <div>
                    {fieldRow("Welcome gesendet", fmtTs(td.welcomeSentAt))}
                    {fieldRow("Erinnerung 1", fmtTs(td.orderReminder1SentAt))}
                    {fieldRow("Erinnerung 2", fmtTs(td.orderReminder2SentAt))}
                    {fieldRow("Erinnerung 3", fmtTs(td.orderReminder3SentAt))}
                    {fieldRow("Antwort am", fmtTs(td.orderReviewedAt))}
                    {fieldRow("Entscheidung", td.orderDecision)}
                  </div>
                </div>
              ) : (
                <p style={{ margin: 0, color: "#a0aec0",
                  fontSize: "0.8rem", fontStyle: "italic" }}>
                  {o.sourceData
                    ? "Testbestellung noch nicht angelegt."
                    : "Quell-Bestellung nicht in Firestore — bitte zuerst scrapen."}
                </p>
              )}

              {/* Review link if welcome sent */}
              {td?.welcomeSentAt && (
                <div style={{
                  marginTop: "0.75em", paddingTop: "0.75em",
                  borderTop: "1px solid #e2e8f0",
                }}>
                  <a
                    href={orderReviewUrl(o.testId)}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      fontSize: "0.8rem", color: "#4a2c2a",
                      fontWeight: "600",
                    }}
                  >
                    Kunden-Link ↗
                  </a>
                  <span style={{ fontSize: "0.78rem", color: "#a0aec0",
                    marginLeft: "0.75em" }}>
                    /order-review/{o.testId}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TestOrdersPage;
